import { supabase } from '../lib/supabase';
import { createNotification, getSentDedupeKeys } from './notificationService';
import { getDisabledTypes } from './notificationPreferences';

// Lembretes de prazo das atividades do Kanban atribuídas a quem está logado.
//
// Roda no navegador, ao abrir o app (e ao voltar pra aba num dia novo): não há
// rotina agendada no banco. Como o aviso aparece no sino, que só existe com o
// app aberto, o efeito pra pessoa é o mesmo. Cada aviso carrega chaves de
// deduplicação no metadata, então rodar de novo não repete nada.

export type DeadlineKind = 'due_tomorrow' | 'due_today' | 'overdue';

export interface DeadlineTask {
  id: string;
  title: string;
  projectId: string;
  dueDate: string; // YYYY-MM-DD
}

export interface PlannedReminder {
  kind: DeadlineKind;
  task: DeadlineTask;
  dedupeKey: string;
}

// Atrasadas há mais que isso não geram aviso: sem esse corte, a primeira
// execução despejaria no sino todo o passivo antigo de uma vez.
export const OVERDUE_WINDOW_DAYS = 7;

// Acima disso, os avisos da rodada viram um resumo só.
export const MAX_INDIVIDUAL_REMINDERS = 3;

export function localDateKey(date: Date): string {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, '0');
  const d = String(date.getDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
}

function addDays(date: Date, days: number): Date {
  const copy = new Date(date.getFullYear(), date.getMonth(), date.getDate());
  copy.setDate(copy.getDate() + days);
  return copy;
}

export function deadlineDedupeKey(kind: DeadlineKind, task: DeadlineTask): string {
  // O prazo entra na chave: se a data mudar, a atividade pode avisar de novo.
  return `${kind}:${task.id}:${task.dueDate}`;
}

export function planDeadlineReminders(
  tasks: DeadlineTask[],
  today: Date,
  alreadySent: Set<string>
): PlannedReminder[] {
  const hoje = localDateKey(today);
  const amanha = localDateKey(addDays(today, 1));
  const limiteAtraso = localDateKey(addDays(today, -OVERDUE_WINDOW_DAYS));

  const planned: PlannedReminder[] = [];
  for (const task of tasks) {
    let kind: DeadlineKind | null = null;
    if (task.dueDate === amanha) kind = 'due_tomorrow';
    else if (task.dueDate === hoje) kind = 'due_today';
    else if (task.dueDate < hoje && task.dueDate >= limiteAtraso) kind = 'overdue';
    if (!kind) continue;

    const dedupeKey = deadlineDedupeKey(kind, task);
    if (alreadySent.has(dedupeKey)) continue;
    planned.push({ kind, task, dedupeKey });
  }
  return planned;
}

// Mesmo critério de concluída do Gantt (ganttStatus.ts), mais a coluna de
// conclusão do Kanban — o quadro marca conclusão movendo para ela.
export function isActivityDone(task: {
  status?: string | null;
  percent_complete?: number | null;
  columnName?: string | null;
}): boolean {
  if ((task.percent_complete ?? 0) >= 100) return true;
  if (task.status === 'completed' || task.status === 'done') return true;
  return /conclu|finaliz|done/i.test(task.columnName ?? '');
}

const TEXTOS: Record<DeadlineKind, { title: string; content: (t: DeadlineTask) => string }> = {
  due_tomorrow: {
    title: 'Atividade vence amanhã',
    content: (t) => `A atividade "${t.title}" vence amanhã.`,
  },
  due_today: {
    title: 'Atividade vence hoje',
    content: (t) => `A atividade "${t.title}" vence hoje.`,
  },
  overdue: {
    title: 'Atividade atrasada',
    content: (t) => `A atividade "${t.title}" passou do prazo.`,
  },
};

function notificationType(kind: DeadlineKind) {
  return kind === 'overdue' ? 'task_overdue' : 'task_due_soon';
}

const rodando = new Set<string>();

function chaveDoDia(organizationId: string, userId: string) {
  return `deadline_reminders_${organizationId}_${userId}`;
}

export async function checkDeadlineReminders(organizationId: string, userId: string): Promise<void> {
  const hoje = localDateKey(new Date());
  const chave = chaveDoDia(organizationId, userId);

  // Uma vez por dia por pessoa. Falha de storage só faz rodar de novo, e a
  // deduplicação segura a repetição.
  try {
    if (localStorage.getItem(chave) === hoje) return;
  } catch {
    // segue sem o atalho
  }
  if (rodando.has(chave)) return;
  rodando.add(chave);

  try {
    // Desligado nas preferências: não gera, e não marca o dia — se religar,
    // a checagem roda na próxima abertura.
    const desligados = await getDisabledTypes(organizationId, userId);
    if (desligados.has('task_due_soon') && desligados.has('task_overdue')) return;

    const limite = localDateKey(addDays(new Date(), 1));
    const { data: tarefas, error } = await supabase
      .from('project_kanban_tasks')
      .select('id, title, due_date, status, percent_complete, column_id, project_id, project:projects!inner(organization_id)')
      .eq('project.organization_id', organizationId)
      .eq('assigned_to', userId)
      .not('due_date', 'is', null)
      .lte('due_date', `${limite}T23:59:59`);
    if (error) throw error;

    const linhas = (tarefas ?? []) as Array<{
      id: string;
      title: string;
      due_date: string;
      status?: string | null;
      percent_complete?: number | null;
      column_id?: string | null;
      project_id: string;
    }>;

    const columnIds = [...new Set(linhas.map(t => t.column_id).filter((id): id is string => !!id))];
    const nomeDaColuna = new Map<string, string>();
    if (columnIds.length > 0) {
      const { data: colunas } = await supabase
        .from('project_kanban_columns')
        .select('id, name')
        .in('id', columnIds);
      for (const c of (colunas ?? []) as Array<{ id: string; name: string }>) {
        nomeDaColuna.set(c.id, c.name);
      }
    }

    const abertas: DeadlineTask[] = linhas
      .filter(t => !isActivityDone({
        status: t.status,
        percent_complete: t.percent_complete,
        columnName: t.column_id ? nomeDaColuna.get(t.column_id) : null,
      }))
      .map(t => ({
        id: t.id,
        title: t.title,
        projectId: t.project_id,
        dueDate: String(t.due_date).slice(0, 10),
      }));

    if (abertas.length > 0) {
      const jaEnviadas = await getSentDedupeKeys(
        organizationId,
        userId,
        ['task_due_soon', 'task_overdue'],
        OVERDUE_WINDOW_DAYS + 7
      );

      const planejados = planDeadlineReminders(abertas, new Date(), jaEnviadas)
        .filter(p => !desligados.has(notificationType(p.kind)));

      if (planejados.length > MAX_INDIVIDUAL_REMINDERS) {
        const atrasadas = planejados.filter(p => p.kind === 'overdue').length;
        const vencendo = planejados.length - atrasadas;
        const partes = [
          atrasadas > 0 ? `${atrasadas} atrasada${atrasadas > 1 ? 's' : ''}` : '',
          vencendo > 0 ? `${vencendo} vencendo hoje ou amanhã` : '',
        ].filter(Boolean);
        await createNotification({
          organization_id: organizationId,
          user_id: userId,
          type: atrasadas > 0 ? 'task_overdue' : 'task_due_soon',
          title: `${planejados.length} atividades pedem atenção`,
          content: `Você tem ${partes.join(' e ')}.`,
          metadata: { source: 'kanban-summary', dedupeKeys: planejados.map(p => p.dedupeKey) },
        });
      } else {
        for (const p of planejados) {
          await createNotification({
            organization_id: organizationId,
            user_id: userId,
            type: notificationType(p.kind),
            title: TEXTOS[p.kind].title,
            content: TEXTOS[p.kind].content(p.task),
            metadata: {
              source: 'kanban',
              projectId: p.task.projectId,
              taskId: p.task.id,
              dedupeKeys: [p.dedupeKey],
            },
          });
        }
      }
    }

    try {
      localStorage.setItem(chave, hoje);
    } catch {
      // sem storage, a próxima abertura confere de novo
    }
  } catch (err) {
    console.warn('Falha ao verificar prazos das atividades:', err);
  } finally {
    rodando.delete(chave);
  }
}
